import React, {useMemo} from 'react';
import {Modal, Platform, StyleSheet, Switch, Text, TouchableOpacity, View} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import FeatherIcon from 'react-native-vector-icons/Feather';
import {ScreenKeyboardAvoider, ModalKeyboardAvoider, ModalScrollBody} from '../KeyboardAware';
import {useTopInset} from '../../helpers/safeArea';
import {BRAND, useManagementTheme, withAlpha} from '../../helpers/managementTheme';
import DemoModeToggleButton from '../DemoModeToggleButton';
import {haptics} from '../../helpers/haptics';

/**
 * Shared "Happy Premium" settings design system.
 *
 * Used by SettingsScreen and the self-service detail screens (profile, password,
 * 2FA, notifications, devices, AI agent, recording…) so they share one visual
 * language: an airy canvas with a faint orange wash, white grouped cards (radius
 * 26, hairline border, very soft shadow), soft *tinted* icon chips instead of
 * saturated gradient squares, orange switches and pill sheet buttons.
 *
 * Theme-aware: every surface, text and divider colour comes from the management
 * theme (`useManagementTheme()`), so the whole settings surface flips to the navy
 * dark palette (`#0A0F1E` / `#131B2F`) in dark mode. The `TILE_GRADIENTS` keys keep
 * their names (callers pass them as `gradient`); only the first stop is used as the
 * chip's tone colour.
 *
 * The sheet ({@link PremiumSheet}) is a SOLID surface — never blurred or glassy.
 */

/** Airy canvas for light mode: a faint orange wash fading into `#F5F6FA`. */
export const SETTINGS_GRADIENT = ['#FFF1E4', '#F5F6FA', '#F5F6FA'];
/** Deep navy canvas for dark mode (aligned with the management dark palette). */
export const SETTINGS_GRADIENT_DARK = ['#1C1520', '#0A0F1E', '#0A0F1E'];
/** Colour stops of the canvas gradients. */
const SETTINGS_GRADIENT_LOCATIONS = [0, 0.3, 1];

/**
 * Curated icon-chip tones, keyed for reuse across rows. Only the first stop of a
 * pair is used as the chip tone; the keys are kept so every call site still
 * works. `indigo` follows the violet tone and `blue` the semantic "info" blue of
 * the premium palette.
 */
export const TILE_GRADIENTS = {
    indigo: ['#7C3AED', '#A78BFA'],
    blue: ['#2563EB', '#60A5FA'],
    green: ['#10B981', '#34D399'],
    orange: [BRAND.base, BRAND.light],
    pink: ['#EC4899', '#F472B6'],
    red: ['#EF4444', '#F87171'],
    teal: ['#14B8A6', '#2DD4BF'],
    purple: ['#8B5CF6', '#A78BFA'],
    slate: ['#64748B', '#94A3B8'],
    amber: [BRAND.base, BRAND.amber],
};

/**
 * Resolve the tone colour of a tile gradient (its first stop).
 * @param {string[]|string} [gradient] Gradient stops or a single colour.
 * @returns {string} Hex colour used for the chip glyph / tint.
 */
const toneOf = (gradient) => {
    if (Array.isArray(gradient) && gradient.length) {
        return gradient[0];
    }

    return typeof gradient === 'string' && gradient ? gradient : BRAND.base;
};

/**
 * Memoised stylesheet bound to the active management theme.
 * @returns {{m: object, styles: Object.<string, object>}} Theme + styles.
 */
const useKitStyles = () => {
    const m = useManagementTheme();
    const styles = useMemo(() => StyleSheet.create(makeStyles(m)), [m]);

    return {m, styles};
};

/**
 * Full-screen airy canvas (faint orange wash fading into the light/navy page
 * colour) with a status-bar safe top pad. Picks the light or dark canvas from the
 * active theme. Keyboard avoidance is built in.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children Screen content.
 * @param {object} [props.style] Extra style merged onto the canvas.
 * @returns {JSX.Element} The canvas.
 */
export const ScreenGradient = ({children, style}) => {
    // Push content below the status bar / notch / Dynamic Island so the premium
    // header never collides with the Island on iPhone 14 Pro and newer (replaces
    // the old static Platform paddingTop of 28/8).
    const topInset = useTopInset();
    const {m, styles} = useKitStyles();

    return (
        <LinearGradient
            colors={m.isDark ? SETTINGS_GRADIENT_DARK : SETTINGS_GRADIENT}
            locations={SETTINGS_GRADIENT_LOCATIONS}
            style={[styles.screen, {paddingTop: topInset + 4}, style]}
        >
            {/* Keyboard-avoidance is baked in here so every settings/self-service
                screen built on ScreenGradient keeps inputs visible above the keyboard
                (iOS padding; Android relies on the activity's adjustResize). */}
            <ScreenKeyboardAvoider>{children}</ScreenKeyboardAvoider>
        </LinearGradient>
    );
};

/**
 * Premium screen header: round back button, left-aligned bold title with an
 * optional subtitle and a right-side accessory.
 *
 * @param {object} props
 * @param {string} props.title Screen title.
 * @param {string} [props.subtitle] Muted line under the title.
 * @param {() => void} [props.onBack] Back handler (renders the round back button).
 * @param {React.ReactNode} [props.right] Right accessory (defaults to the self-gating Demo toggle).
 * @returns {JSX.Element} The header.
 */
export const PremiumHeader = ({title, subtitle, onBack, right}) => {
    const {m, styles} = useKitStyles();

    return (
        <View style={styles.header}>
            {onBack ? (
                <TouchableOpacity
                    onPress={onBack}
                    style={styles.backBtn}
                    activeOpacity={0.8}
                    hitSlop={10}
                    accessibilityRole="button"
                >
                    <FeatherIcon name="arrow-left" size={21} color={m.colors.heading} />
                </TouchableOpacity>
            ) : <View style={styles.backBtnSpacer} />}
            <View style={styles.headerTextWrap}>
                <Text numberOfLines={1} style={styles.headerTitle}>{title}</Text>
                {!!subtitle && <Text numberOfLines={1} style={styles.headerSubtitle}>{subtitle}</Text>}
            </View>
            {/* Top-right slot: caller-provided action, else the self-gating Demo toggle. */}
            <View style={styles.headerRight}>{right || <DemoModeToggleButton color={m.colors.subText} />}</View>
        </View>
    );
};

/**
 * A soft tinted icon chip (Feather glyph in the tone colour on a 12 % tint of the
 * same tone). The tone is the first stop of `gradient`, so every existing
 * `TILE_GRADIENTS.*` call site keeps working with the calmer premium look.
 *
 * @param {object} props
 * @param {string} props.name Feather icon name.
 * @param {string[]} [props.gradient] Tile gradient; its first stop is the tone.
 * @param {number} [props.size=38] Chip edge.
 * @param {number} [props.icon=18] Glyph size.
 * @param {string} [props.color] Explicit glyph colour (overrides the tone).
 * @returns {JSX.Element} The chip.
 */
export const GradientIcon = ({name, gradient = TILE_GRADIENTS.orange, size = 38, icon = 18, color}) => {
    const {m, styles} = useKitStyles();
    const tone = toneOf(gradient);

    return (
        <View
            style={[
                styles.tile,
                {
                    width: size,
                    height: size,
                    borderRadius: Math.round(size * 0.34),
                    backgroundColor: withAlpha(tone, m.isDark ? 0.22 : 0.12),
                },
            ]}
        >
            <FeatherIcon name={name} size={icon} color={color || tone} />
        </View>
    );
};

/**
 * The one bold highlight of a settings screen: the brand-orange gradient card
 * with a frosted icon disc, a big white title and a supporting line. Replaces the
 * old "big icon tile + centred title" hero block.
 *
 * @param {object} props
 * @param {string} props.icon Feather icon name shown on the frosted disc.
 * @param {string} props.title Headline (white, bold).
 * @param {string} [props.subtitle] Supporting line (white, 88 %).
 * @param {React.ReactNode} [props.children] Extra content under the text (e.g. a pill).
 * @param {object} [props.style] Extra style merged onto the card.
 * @returns {JSX.Element} The hero card.
 */
export const SettingsHero = ({icon, title, subtitle, children, style}) => {
    const {styles} = useKitStyles();

    return (
        <LinearGradient
            colors={BRAND.gradient}
            start={{x: 0, y: 0}}
            end={{x: 1, y: 1}}
            style={[styles.hero, style]}
        >
            <View pointerEvents="none" style={styles.heroBlobA} />
            <View pointerEvents="none" style={styles.heroBlobB} />
            <View style={styles.heroRow}>
                {!!icon && (
                    <View style={styles.heroDisc}>
                        <FeatherIcon name={icon} size={26} color="#FFFFFF" />
                    </View>
                )}
                <View style={styles.heroText}>
                    <Text style={styles.heroTitle} numberOfLines={2}>{title}</Text>
                    {!!subtitle && <Text style={styles.heroSubtitle} numberOfLines={3}>{subtitle}</Text>}
                </View>
            </View>
            {children}
        </LinearGradient>
    );
};

/**
 * A grouped white card with an optional sentence-case title + subtitle header
 * (with a tinted icon chip) and a body of rows/children.
 *
 * @param {object} props
 * @param {string} [props.title] Section title.
 * @param {string} [props.subtitle] Muted supporting line.
 * @param {string} [props.icon] Feather glyph of the header chip.
 * @param {string[]} [props.iconGradient] Chip tone (first stop).
 * @param {React.ReactNode} props.children Card body.
 * @param {object} [props.style] Extra style merged onto the wrapper.
 * @returns {JSX.Element} The section.
 */
export const SectionCard = ({title, subtitle, icon, iconGradient, children, style}) => {
    const {styles} = useKitStyles();

    return (
        <View style={[styles.section, style]}>
            {(title || subtitle) && (
                <View style={styles.sectionHead}>
                    {icon && <GradientIcon name={icon} gradient={iconGradient} size={32} icon={16} />}
                    <View style={styles.sectionHeadText}>
                        {!!title && <Text style={styles.sectionTitle}>{title}</Text>}
                        {!!subtitle && <Text style={styles.sectionSubtitle}>{subtitle}</Text>}
                    </View>
                </View>
            )}
            <View style={styles.card}>{children}</View>
        </View>
    );
};

/**
 * A single tappable settings row: tinted icon chip, label + optional sublabel,
 * optional right value/accessory and chevron.
 *
 * @param {object} props
 * @param {string} props.icon Feather icon name
 * @param {string[]} [props.gradient] icon chip tone (first stop)
 * @param {string} props.label primary label
 * @param {string} [props.sublabel] secondary description line
 * @param {string} [props.value] right-aligned value text
 * @param {React.ReactNode} [props.accessory] right-side element (e.g. a Switch) — replaces chevron
 * @param {boolean} [props.chevron=true] show the chevron
 * @param {boolean} [props.last] omit the bottom divider
 * @param {boolean} [props.danger] tint the label red (destructive)
 * @param {boolean} [props.disabled] mute the row and ignore taps
 * @param {() => void} [props.onPress]
 * @returns {JSX.Element} The row.
 */
export const SettingRow = ({
    icon,
    gradient = TILE_GRADIENTS.slate,
    label,
    sublabel,
    value,
    accessory,
    chevron = true,
    last = false,
    danger = false,
    disabled = false,
    onPress,
}) => {
    const {m, styles} = useKitStyles();
    const Container = onPress ? TouchableOpacity : View;

    return (
        <Container
            {...(onPress ? {activeOpacity: 0.7, onPress, disabled} : {})}
            style={[styles.row, last && styles.rowLast, disabled && {opacity: 0.5}]}
        >
            {icon ? <GradientIcon name={icon} gradient={danger ? TILE_GRADIENTS.red : gradient} /> : null}
            <View style={styles.rowText}>
                <Text style={[styles.rowLabel, danger && styles.rowLabelDanger]} numberOfLines={1}>{label}</Text>
                {!!sublabel && <Text style={styles.rowSublabel} numberOfLines={2}>{sublabel}</Text>}
            </View>
            {!!value && <Text style={styles.rowValue} numberOfLines={1}>{value}</Text>}
            {accessory ? accessory : (chevron && onPress ? (
                <FeatherIcon name="chevron-right" size={19} color={m.colors.muted} style={{marginLeft: 6}} />
            ) : null)}
        </Container>
    );
};

/**
 * Brand switch: orange track when on, neutral when off, white thumb. Drop-in for
 * the RN `Switch` (every prop is forwarded) so settings screens get the premium
 * toggle without re-declaring colours.
 *
 * @param {object} props Same props as the React Native `Switch`.
 * @param {boolean} props.value Current value.
 * @param {(value: boolean) => void} [props.onValueChange] Change handler.
 * @param {boolean} [props.disabled] Disable the switch.
 * @returns {JSX.Element} The switch.
 */
export const PremiumSwitch = ({value, onValueChange, disabled, ...rest}) => {
    const m = useManagementTheme();
    const off = m.isDark ? '#334155' : '#D5DAE5';

    return (
        <Switch
            value={!!value}
            onValueChange={(next) => {
                haptics.select();
                onValueChange?.(next);
            }}
            disabled={disabled}
            trackColor={{false: off, true: BRAND.base}}
            thumbColor="#FFFFFF"
            activeThumbColor="#FFFFFF"
            ios_backgroundColor={off}
            {...rest}
        />
    );
};

/**
 * Premium bottom-sheet modal — a SOLID, themed surface (radius 28, no blur, no
 * glass) that slides up from the bottom with a dimmed scrim. Built on the plain
 * React Native `Modal` (NOT the react-native-paper Dialog) so it renders our own
 * controlled surface and text colours.
 *
 * @param {object} props
 * @param {boolean} props.visible whether the sheet is shown
 * @param {() => void} props.onClose dismiss handler (scrim tap, back, cancel)
 * @param {string} [props.title] sheet heading
 * @param {string} [props.subtitle] supporting line under the heading
 * @param {string} [props.cancelText] when set, renders a full-width pill cancel button
 * @param {React.ReactNode} props.children sheet body
 * @returns {JSX.Element} The sheet.
 */
export const PremiumSheet = ({visible, onClose, title, subtitle, cancelText, children}) => {
    const {styles} = useKitStyles();

    return (
        <Modal visible={visible} transparent animationType="slide" statusBarTranslucent onRequestClose={onClose}>
            {/* ModalKeyboardAvoider lifts the sheet above the keyboard on Android too
                (a RN Modal does not inherit the activity's adjustResize). */}
            <ModalKeyboardAvoider justify="flex-end" style={styles.sheetOverlay}>
                <TouchableOpacity style={StyleSheet.absoluteFill} activeOpacity={1} onPress={onClose} />
                <View style={styles.sheet}>
                    <View style={styles.sheetHandle} />
                    {(title || subtitle) && (
                        <View style={styles.sheetHead}>
                            {!!title && <Text style={styles.sheetTitle}>{title}</Text>}
                            {!!subtitle && <Text style={styles.sheetSubtitle}>{subtitle}</Text>}
                        </View>
                    )}
                    {/* Scrollable so tall sheets never clip and fields reach above the keyboard. */}
                    <ModalScrollBody contentContainerStyle={styles.sheetBody}>
                        {children}
                        {!!cancelText && (
                            <TouchableOpacity style={styles.sheetCancel} activeOpacity={0.8} onPress={onClose}>
                                <Text style={styles.sheetCancelText}>{cancelText}</Text>
                            </TouchableOpacity>
                        )}
                    </ModalScrollBody>
                </View>
            </ModalKeyboardAvoider>
        </Modal>
    );
};

/**
 * A single tappable option row for use inside {@link PremiumSheet}: a tinted icon
 * chip, label + optional sublabel and a trailing chevron.
 *
 * @param {object} props
 * @param {string} props.icon Feather icon name
 * @param {string[]} [props.gradient] icon chip tone (first stop)
 * @param {string} props.label primary label
 * @param {string} [props.sublabel] secondary description line
 * @param {boolean} [props.last] omit the bottom divider
 * @param {() => void} props.onPress
 * @returns {JSX.Element} The option row.
 */
export const SheetOption = ({icon, gradient = TILE_GRADIENTS.slate, label, sublabel, last = false, onPress}) => {
    const {m, styles} = useKitStyles();

    return (
        <TouchableOpacity
            style={[styles.sheetOption, last && {borderBottomWidth: 0}]}
            activeOpacity={0.7}
            onPress={onPress}
        >
            <GradientIcon name={icon} gradient={gradient} size={42} icon={19} />
            <View style={styles.sheetOptionText}>
                <Text style={styles.sheetOptionLabel} numberOfLines={1}>{label}</Text>
                {!!sublabel && <Text style={styles.sheetOptionSub} numberOfLines={2}>{sublabel}</Text>}
            </View>
            <FeatherIcon name="chevron-right" size={19} color={m.colors.muted} />
        </TouchableOpacity>
    );
};

/**
 * Style factory for the whole settings kit. Layout metrics are fixed; colours come
 * from the management theme so light and dark stay consistent with the etalon.
 * @param {object} m Result of `useManagementTheme()`.
 * @returns {Object.<string, object>} Style map.
 */
const makeStyles = (m) => ({
    screen: {
        // Top padding is applied dynamically from the safe-area inset in
        // ScreenGradient (notch / Dynamic Island aware), not statically here.
        flex: 1,
    },
    sheetOverlay: {flex: 1, justifyContent: 'flex-end', backgroundColor: 'rgba(15,23,42,0.5)'},
    sheetBody: {paddingBottom: 4},
    sheet: {
        backgroundColor: m.colors.surface,
        borderTopLeftRadius: 28,
        borderTopRightRadius: 28,
        // Cap height so the inner ScrollView can scroll tall content instead of
        // pushing the sheet off-screen.
        maxHeight: '88%',
        paddingHorizontal: 20,
        paddingTop: 10,
        paddingBottom: Platform.OS === 'ios' ? 34 : 22,
        shadowColor: m.isDark ? '#000' : '#1E2A4A',
        shadowOffset: {width: 0, height: -8},
        shadowOpacity: m.isDark ? 0.4 : 0.16,
        shadowRadius: 24,
        elevation: 24,
    },
    sheetHandle: {
        alignSelf: 'center',
        width: 44,
        height: 5,
        borderRadius: 3,
        backgroundColor: m.colors.border,
        marginBottom: 14,
    },
    sheetHead: {marginBottom: 6, paddingHorizontal: 2},
    sheetTitle: {fontSize: 20, fontWeight: '800', color: m.colors.heading, letterSpacing: -0.3},
    sheetSubtitle: {fontSize: 13, fontWeight: '500', color: m.colors.subText, marginTop: 3, lineHeight: 18},
    sheetOption: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 13,
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: m.colors.border,
    },
    sheetOptionText: {flex: 1, marginLeft: 14},
    sheetOptionLabel: {fontSize: 16, fontWeight: '700', color: m.colors.heading},
    sheetOptionSub: {fontSize: 12.5, fontWeight: '500', color: m.colors.subText, marginTop: 2, lineHeight: 16},
    sheetCancel: {
        marginTop: 14,
        backgroundColor: m.colors.surfaceAlt,
        borderRadius: 999,
        paddingVertical: 15,
        alignItems: 'center',
    },
    sheetCancelText: {fontSize: 15.5, fontWeight: '700', color: m.colors.subText},
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 10,
    },
    backBtn: {
        width: 42,
        height: 42,
        borderRadius: 21,
        backgroundColor: m.colors.surface,
        borderWidth: 1,
        borderColor: m.colors.border,
        alignItems: 'center',
        justifyContent: 'center',
        ...m.shadow(1),
    },
    backBtnSpacer: {width: 4, height: 42},
    headerTextWrap: {flex: 1, alignItems: 'flex-start', paddingHorizontal: 12},
    headerTitle: {fontSize: 22, fontWeight: '800', color: m.colors.heading, letterSpacing: -0.4},
    headerSubtitle: {fontSize: 12.5, fontWeight: '600', color: m.colors.subText, marginTop: 1},
    headerRight: {minWidth: 42, height: 42, alignItems: 'flex-end', justifyContent: 'center'},
    section: {marginHorizontal: 16, marginTop: 20},
    sectionHead: {flexDirection: 'row', alignItems: 'center', marginBottom: 10, paddingHorizontal: 4},
    sectionHeadText: {marginLeft: 10, flex: 1},
    sectionTitle: {fontSize: 17, fontWeight: '800', color: m.colors.heading, letterSpacing: -0.2},
    sectionSubtitle: {fontSize: 12.5, fontWeight: '500', color: m.colors.subText, marginTop: 1, lineHeight: 16},
    card: {
        backgroundColor: m.colors.surface,
        borderRadius: 26,
        borderWidth: 1,
        borderColor: m.colors.border,
        paddingHorizontal: 16,
        ...m.shadow(1),
    },
    tile: {alignItems: 'center', justifyContent: 'center'},
    hero: {
        marginHorizontal: 16,
        marginTop: 8,
        borderRadius: 28,
        padding: 20,
        overflow: 'hidden',
        shadowColor: BRAND.dark,
        shadowOffset: {width: 0, height: 14},
        shadowOpacity: 0.26,
        shadowRadius: 22,
        elevation: 8,
    },
    heroBlobA: {position: 'absolute', width: 200, height: 200, borderRadius: 100, backgroundColor: 'rgba(255,255,255,0.12)', top: -80, right: -60},
    heroBlobB: {position: 'absolute', width: 140, height: 140, borderRadius: 70, backgroundColor: 'rgba(255,255,255,0.09)', bottom: -60, left: -40},
    heroRow: {flexDirection: 'row', alignItems: 'center'},
    heroDisc: {width: 56, height: 56, borderRadius: 28, backgroundColor: 'rgba(255,255,255,0.24)', alignItems: 'center', justifyContent: 'center'},
    heroText: {flex: 1, marginLeft: 14},
    heroTitle: {fontSize: 20, lineHeight: 25, fontWeight: '800', letterSpacing: -0.4, color: '#FFFFFF'},
    heroSubtitle: {fontSize: 13.5, lineHeight: 19, fontWeight: '500', color: 'rgba(255,255,255,0.88)', marginTop: 4},
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 13,
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: m.colors.border,
    },
    rowLast: {borderBottomWidth: 0},
    rowText: {flex: 1, marginLeft: 13},
    rowLabel: {fontSize: 15.5, fontWeight: '700', color: m.colors.heading},
    rowLabelDanger: {color: m.colors.danger, fontWeight: '700'},
    rowSublabel: {fontSize: 12.5, fontWeight: '500', color: m.colors.subText, marginTop: 2, lineHeight: 16},
    rowValue: {fontSize: 14, fontWeight: '700', color: m.colors.subText, marginLeft: 6, maxWidth: 130},
});

export default {ScreenGradient, PremiumHeader, GradientIcon, SettingsHero, SectionCard, SettingRow, PremiumSwitch, PremiumSheet, SheetOption, TILE_GRADIENTS, SETTINGS_GRADIENT, SETTINGS_GRADIENT_DARK};
